/**
 * Assigns a unique outline ring color to every team member so two avatars
 * never look accidentally alike, even when their chip backgrounds are
 * similar. Each outline is also chosen to have meaningful RGB distance
 * from the member's own chip color, so the ring reads as a ring and not
 * a blurred edge.
 */

const OUTLINE_PALETTE = [
  '#f59e0b', // amber
  '#06b6d4', // cyan
  '#a855f7', // purple
  '#84cc16', // lime
  '#ec4899', // pink
  '#eab308', // yellow
  '#14b8a6', // teal
  '#ef4444', // red
  '#0ea5e9', // sky
  '#22c55e', // green
  '#f43f5e', // rose
  '#8b5cf6', // violet
];

const MIN_CHIP_OUTLINE_DISTANCE = 100;

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function parseHex(h: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(h.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function rgbDistance(a: string, b: string): number {
  const p = parseHex(a);
  const q = parseHex(b);
  if (!p || !q) return Infinity;
  return Math.sqrt(
    (p[0] - q[0]) ** 2 + (p[1] - q[1]) ** 2 + (p[2] - q[2]) ** 2
  );
}

export function assignOutlineColors(
  members: Array<{ userId: string; color?: string }>
): Record<string, string> {
  // Sorted iteration so outline assignment is stable across renders /
  // regardless of input order.
  const sorted = [...members].sort((a, b) => a.userId.localeCompare(b.userId));
  const result: Record<string, string> = {};
  const used = new Set<string>();
  for (const m of sorted) {
    const chip = m.color ?? '';
    const startIdx = hashStr(m.userId) % OUTLINE_PALETTE.length;
    let picked: string | null = null;
    for (let offset = 0; offset < OUTLINE_PALETTE.length; offset++) {
      const candidate =
        OUTLINE_PALETTE[(startIdx + offset) % OUTLINE_PALETTE.length];
      if (used.has(candidate)) continue;
      if (rgbDistance(candidate, chip) < MIN_CHIP_OUTLINE_DISTANCE) continue;
      picked = candidate;
      break;
    }
    // >12 members, or all remaining slots too close to this chip — fall back
    // to the hashed slot. Team size would be the real fix at that point.
    if (!picked) picked = OUTLINE_PALETTE[startIdx];
    result[m.userId] = picked;
    used.add(picked);
  }
  return result;
}
